import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardHeader } from '@/components/Card';
import { usePortalAuth } from '@/portal/auth.store';
import {
  anexarArquivo,
  anexos as buscarAnexos,
  baixarAnexoDoChamado,
  chamado as buscarChamado,
  comentar,
  comentarios as buscarComentarios,
} from '@/portal/portal.api';
import { dataHora, duracao, prazoEmPalavras, rotuloDeMetrica, tamanhoDeArquivo } from '@/portal/formato';

/**
 * Converte texto livre do textarea para HTML mínimo.
 * O servidor sanitiza no armazenamento; aqui só preservamos a estrutura visual.
 */
function textoParaHtml(texto: string): string {
  return texto
    .split(/\n\n+/)
    .map((paragrafo) => `<p>${paragrafo.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * O chamado, do ponto de vista do cliente.
 *
 * Linha do tempo **pública** — a API só devolve comentário `PUBLIC`, e o filtro
 * é de consulta, não de renderização: o texto interno nem chega a este processo.
 * A tela não tem, e não pode ter, um "mostrar notas internas".
 */
export function ChamadoPage({ id }: { id: string }) {
  const cliente = useQueryClient();
  const [rascunho, setRascunho] = useState('');

  const chamado = useQuery({ queryKey: ['portal', 'chamado', id], queryFn: () => buscarChamado(id) });
  const conversa = useQuery({
    queryKey: ['portal', 'comentarios', id],
    queryFn: () => buscarComentarios(id),
  });
  const arquivos = useQuery({
    queryKey: ['portal', 'anexos', id],
    queryFn: () => buscarAnexos(id),
  });

  const responder = useMutation({
    mutationFn: (texto: string) => comentar(id, textoParaHtml(texto)),
    onSuccess: async () => {
      setRascunho('');
      await cliente.invalidateQueries({ queryKey: ['portal', 'comentarios', id] });
    },
  });

  const anexar = useMutation({
    mutationFn: (arquivo: File) => anexarArquivo(id, arquivo),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['portal', 'anexos', id] });
    },
  });

  const podeComentar = usePortalAuth(
    (estado) => estado.contato?.permissoes.includes('portal.ticket.comment') ?? false,
  );
  const podeAnexar = usePortalAuth(
    (estado) => estado.contato?.permissoes.includes('portal.ticket.attach') ?? false,
  );

  if (chamado.isPending) return <p className="text-sm text-muted-foreground">Carregando…</p>;
  if (chamado.isError || !chamado.data) {
    return <p className="text-sm text-muted-foreground">Chamado não encontrado.</p>;
  }

  const dados = chamado.data;

  function enviarResposta(evento: FormEvent) {
    evento.preventDefault();
    const texto = rascunho.trim();
    if (texto.length > 0) responder.mutate(texto);
  }

  return (
    <div className="space-y-4">
      <header>
        <p className="font-mono text-xs text-muted-foreground">{dados.numero}</p>
        <h1 className="text-xl font-semibold">{dados.assunto}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {dados.situacao}
          {dados.servico ? ` · ${dados.servico.nome}` : ''} · aberto em {dataHora(dados.abertoEm)}
        </p>
      </header>

      {dados.descricaoHtml ? (
        <Card className="px-5 py-4">
          {/* O HTML já foi higienizado no servidor, na gravação. */}
          <div
            className="prose-sm max-w-none text-sm"
            dangerouslySetInnerHTML={{ __html: dados.descricaoHtml }}
          />
        </Card>
      ) : null}

      {dados.sla.length > 0 ? (
        <Card>
          <CardHeader title="Prazos" description="Os compromissos do seu contrato." />
          <ul className="divide-y divide-border">
            {dados.sla.map((compromisso) => (
              <li
                key={compromisso.metrica}
                className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-3 text-sm"
              >
                <span>{rotuloDeMetrica(compromisso.metrica)}</span>
                <span className="text-muted-foreground">
                  meta {duracao(compromisso.metaMinutos)} ·{' '}
                  {compromisso.cumprido === true
                    ? 'cumprido'
                    : prazoEmPalavras(compromisso.restanteMinutos)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        <CardHeader title="Conversa" />
        <ul className="divide-y divide-border">
          {conversa.data?.map((comentario) => (
            <li key={comentario.id} className="px-5 py-4">
              <p className="text-xs text-muted-foreground">
                {comentario.daAptum ? 'Aptum' : (comentario.autor?.nome ?? 'Você')} ·{' '}
                {dataHora(comentario.criadoEm)}
              </p>
              <div
                className="mt-1 text-sm"
                dangerouslySetInnerHTML={{ __html: comentario.corpoHtml }}
              />
            </li>
          ))}
          {conversa.data?.length === 0 ? (
            <li className="px-5 py-4 text-sm text-muted-foreground">
              Nenhuma mensagem ainda.
            </li>
          ) : null}
        </ul>

        {podeComentar ? (
          <form className="space-y-2 border-t border-border px-5 py-4" onSubmit={enviarResposta}>
            <label className="block text-sm font-medium" htmlFor="resposta">
              Responder
            </label>
            <textarea
              id="resposta"
              rows={3}
              value={rascunho}
              onChange={(e) => setRascunho(e.target.value)}
              className="w-full rounded-md border border-border bg-background p-2 text-sm"
            />
            <Button type="submit" size="sm" loading={responder.isPending}>
              Enviar
            </Button>
          </form>
        ) : null}
      </Card>

      <Card>
        <CardHeader title="Anexos" />
        <ul className="divide-y divide-border">
          {arquivos.data?.map((anexo) => (
            <li
              key={anexo.id}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm"
            >
              <span className="inline-flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden />
                {anexo.nomeDoArquivo}
              </span>
              <span className="text-xs text-muted-foreground">
                {tamanhoDeArquivo(anexo.tamanhoBytes)} ·{' '}
                {anexo.disponivel ? (
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={() => void baixarAnexoDoChamado(id, anexo.id, anexo.nomeDoArquivo)}
                  >
                    baixar
                  </button>
                ) : (
                  'aguardando verificação'
                )}
              </span>
            </li>
          ))}
          {arquivos.data?.length === 0 ? (
            <li className="px-5 py-3 text-sm text-muted-foreground">Nenhum anexo.</li>
          ) : null}
        </ul>

        {podeAnexar ? (
          <div className="border-t border-border px-5 py-4">
            <input
              type="file"
              className="text-sm"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                if (arquivo) anexar.mutate(arquivo);
              }}
            />
            {anexar.isError ? (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {(anexar.error as Error).message}
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
